// Constants
import { ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { PlannerDefaultPlanId } from "@ms/uno-constants/lib/local/ServiceConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { DefaultMyTasksViewPivotType, MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { getFilteredTasksForPivot } from "@ms/planner-stores/lib/local/utilities/MyTasksFilterUtilities";
// Utilities
import { extractMyTasksRoutingDetailsFromSubRoute } from "@ms/uno-routing";
import { getDefaultGroupingOption } from "@ms/uno-utilities/lib/local/ViewUtilities";
/**
 * Method to calculate the my tasks page render state
 * @param storeLoader store loader
 * @param configProvider config provider
 */ export function calculateMyTasksRenderState(storeLoader, configProvider) {
    const routeStore = storeLoader.load(Store.Route);
    const config = configProvider();
    const myTasksRoutingDetails = extractMyTasksRoutingDetailsFromSubRoute(routeStore.getCurrentSubRoute(), routeStore.getCurrentSubRouteType());
    const currentPivot = myTasksRoutingDetails?.pivotType ?? DefaultMyTasksViewPivotType;
    const currentViewType = myTasksRoutingDetails?.viewType || ViewType.Board;
    switch(currentPivot){
        case MyTasksViewPivotType.PrivateTasks:
            return getPrivateTasksViewRenderState(storeLoader, config, currentViewType);
        case MyTasksViewPivotType.All:
        case MyTasksViewPivotType.AssignedToMe:
        case MyTasksViewPivotType.DueSoon:
            return getAssignedToMeViewRenderState(storeLoader, config, currentViewType);
        case MyTasksViewPivotType.ThisWeek:
        case MyTasksViewPivotType.Important:
            return getAssignedToMeViewRenderState(storeLoader, config, currentViewType, currentPivot);
        case MyTasksViewPivotType.FlaggedEmails:
            return getFlaggedEmailsViewRenderState(storeLoader, config, currentViewType);
        default:
            return ViewRenderState.None;
    }
}
/**
 * Get render state for a plan-based view (reusable for private tasks and flagged emails)
 */ function getPlanBasedRenderState(storeLoader, config, viewType, planIdGetter) {
    const planStore = storeLoader.load(Store.Plan);
    const planId = planIdGetter(planStore);
    if (!planId) {
        return ViewRenderState.None;
    }
    return getPlanDataState(storeLoader, config, viewType, planId);
}
/**
 * Get the private tasks view data state
 */ function getPrivateTasksViewRenderState(storeLoader, config, viewType) {
    const taskStore = storeLoader.load(Store.Tasks);
    const result = taskStore.getTasksForPlan(PlannerDefaultPlanId);
    if (!result) {
        return ViewRenderState.None;
    }
    const filteredTasks = getFilteredTasksForMyTasks(storeLoader, viewType, result.data ?? []);
    const isDataFinalized = result.status.state !== StoreEntityState.Absent;
    const isDataLoaded = filteredTasks.length >= config.settings.performanceSettings.minimumTasksForVisualReadiness;
    return isDataLoaded || isDataFinalized ? ViewRenderState.Critical : ViewRenderState.None;
}
/**
 * Get the flagged emails view data state
 */ function getFlaggedEmailsViewRenderState(storeLoader, config, viewType) {
    return getPlanBasedRenderState(storeLoader, config, viewType, (planStore)=>planStore.getFlaggedEmailPlanId());
}
/**
 * Get the plan data state
 */ function getPlanDataState(storeLoader, config, viewType, planId) {
    const taskStore = storeLoader.load(Store.Tasks);
    const storeReadResult = taskStore.getTasksForPlan(planId);
    const filteredTasks = getFilteredTasksForMyTasks(storeLoader, viewType, storeReadResult?.data ?? []);
    const isDataFinalized = storeReadResult ? storeReadResult.status.state !== StoreEntityState.Absent : false;
    const isDataLoaded = filteredTasks.length >= config.settings.performanceSettings.minimumTasksForVisualReadiness;
    return isDataLoaded || isDataFinalized ? ViewRenderState.Critical : ViewRenderState.None;
}
/**
 * Helper function to get filtered tasks based on the viewType
 */ function getFilteredTasksForMyTasks(storeLoader, viewType, tasks) {
    const taskStore = storeLoader.load(Store.Tasks);
    const viewId = {
        name: ViewName.MyTasksPage,
        partitionKey: storeLoader.load(Store.AppContext).getAppContext().userId ?? ""
    };
    let filteredTasks = [];
    if (viewType === ViewType.Board) {
        const boardViewData = taskStore.getTasksForBoardView(viewId, getDefaultGroupingOption(viewId));
        if (boardViewData) {
            filteredTasks = Object.keys(boardViewData).reduce((allTasks, key)=>{
                const groupTasks = boardViewData[key];
                return allTasks.concat(groupTasks || []);
            }, []);
        }
    } else if (viewType === ViewType.Grid) {
        filteredTasks = taskStore.getTasksForGridView(viewId).flattenedTasks;
    } else {
        filteredTasks = tasks;
    }
    return filteredTasks;
}
/**
 * Helper function to check non-critical stores data status
 */ function checkNonCriticalStoresData(storeLoader) {
    const [appContextStore, tenantStore, upsellStore] = storeLoader.loadAll([
        Store.AppContext,
        Store.Tenant,
        Store.Upsell
    ]);
    const isAppContextStoreDataFetched = appContextStore.getTodoSettings()?.status.state !== StoreEntityState.Absent;
    const isTenantStoreDataFetched = tenantStore.getCurrentTenantFeedbackSettings()?.status.state !== StoreEntityState.Absent;
    const isUpsellStoreDataFetched = upsellStore.getUserProjectSubscriptionStatus()?.status.state !== StoreEntityState.Absent;
    return isAppContextStoreDataFetched && isTenantStoreDataFetched && isUpsellStoreDataFetched;
}
/**
 * Helper function to aggregate all user tasks based on feature settings
 */ function aggregateUserTasks(config, assignedToMeTasks, storeLoader) {
    // Do not fetch private tasks and flagged emails tasks if todo experience is not enabled in the environment
    if (!config.settings?.EnvironmentFeatureSettings?.EnableTodoExperience) {
        return assignedToMeTasks;
    }
    const [taskStore, planStore] = storeLoader.loadAll([
        Store.Tasks,
        Store.Plan
    ]);
    const privateTasks = taskStore.getTasksForPlan(PlannerDefaultPlanId)?.data ?? [];
    const flaggedPlanId = planStore.getFlaggedEmailPlanId();
    const flaggedTasks = flaggedPlanId ? taskStore.getTasksForPlan(flaggedPlanId)?.data ?? [] : [];
    return [
        ...assignedToMeTasks,
        ...privateTasks,
        ...flaggedTasks
    ];
}
/**
 * Get the assigned to me tasks view data state
 */ function getAssignedToMeViewRenderState(storeLoader, config, viewType, visibleTasksPivot) {
    const [appContextStore, taskStore] = storeLoader.loadAll([
        Store.AppContext,
        Store.Tasks
    ]);
    const currentUserId = appContextStore.getAppContext().userId;
    if (!currentUserId) {
        return ViewRenderState.None;
    }
    // Get critical data from stores
    const storeReadResult = taskStore.getTasksAssignedToUser(currentUserId);
    const assignedToMeTasks = storeReadResult?.data ?? [];
    const tasks = aggregateUserTasks(config, assignedToMeTasks, storeLoader);
    const filteredTasks = visibleTasksPivot ? getFilteredTasksForPivot(visibleTasksPivot, currentUserId, {
        taskStore,
        planStore: storeLoader.load(Store.Plan),
        pinnedTasksStore: storeLoader.load(Store.PinnedTasks),
        isStarredTaskPropertyEnabled: config.flights.EnableIsStarredTaskProperty
    }) : getFilteredTasksForMyTasks(storeLoader, viewType, tasks);
    const minimumTasksNeeded = config.settings.performanceSettings.minimumTasksForVisualReadiness;
    const isDataFinalized = storeReadResult ? storeReadResult.status.state !== StoreEntityState.Absent : false;
    // Data is considered loaded if the minimum number of tasks required for visual readiness are available,
    // plan data for those tasks is lazy loaded
    const isDataLoaded = filteredTasks.length >= minimumTasksNeeded;
    // Get non-critical data from stores using helper function
    const allNonCriticalDataFetched = checkNonCriticalStoresData(storeLoader);
    if (allNonCriticalDataFetched && isDataFinalized) {
        return ViewRenderState.All;
    }
    if (isDataLoaded || isDataFinalized) {
        return ViewRenderState.Critical;
    }
    return ViewRenderState.None;
}
